import {
    APPROXIMATE_FACILITY_LOCATION,
    RESEND_UNCONFIRMED_TIMEFRAME_CALLS,
    TIMEFRAME_CONFIRMATION_PREFERENCE,
} from '@/constants/featureFlags';

// Toggle definitions for Account/Communications, shared by Profile and the corporate shipper page.
// The values live on `users.circles`, which Communications reads from context.

const TIMEFRAME_CONFIRMATION_OPTIONS = [
    { value: 'sms', label: 'Text & email' },
    { value: 'robocall', label: 'Robocall' },
    { value: 'none', label: 'Do not send' },
    { value: 'autoConfirm', label: 'Autoconfirm timeframes' },
];

export const SHIPPER_COMMS = [
    {
        featureFlag: 'disable-review-sms-messages',
        display: 'Disable Carrier Review SMS Messages',
        description: 'Disables carrier review SMS messages sent to customers upon order completion.',
    },
    {
        featureFlag: 'curbside-no-timeframe',
        display: 'Exclude Timeframe from Confirmation Text on Curbside Stops',
        description: `Enabling this flag will remove the 2/4 time window on the planning confirmation text for curbside stops when submitting a route. The below flag must not be enabled as this only applies when curbside stops are automatically confirmed.`,
    },
    {
        featureFlag: 'curbside-no-autoconfirm',
        display: 'Require Confirmation on Curbside Stops',
        description: `By default curbside deliveries are auto confirmed. Be enabling this flag, curbside deliveries will no longer be auto confirmed.`,
    },
    {
        featureFlag: 'autoconfirm-all-stops',
        display: 'Automatically Confirm all Stops',
        description: `This flag will autocomplete all stops and bypass the need to manually confirm stop timeframes.`,
    },
    {
        featureFlag: 'sms-alert-next-stop-customers',
        display: 'Next Stop Customer Alert',
        description: 'This is an sms alert to customers on an FTL route when they are the next stop on the route',
    },
    {
        featureFlag: 'customer-emails-with-sms',
        display: 'Send Email Notifications in Addition to SMS',
        description:
            'Whenever SMS communications are sent to customers, an email will be sent to the customer as well with the corresponding information.',
    },
    {
        featureFlag: 'disable-confirm-timeframe-email',
        display: 'Disable Confirm Time Frame Email',
        description:
            'Disables the email that is sent to customers that asks them to confirm their set time frame from when you submit a route from the planning page',
    },
    {
        featureFlag: 'disable-customer-availability',
        display: 'Disable Predelivery Survey and Customer Availability',
        description: 'Disables the SMS asking users basic predelivery and customer availability information',
    },
    {
        featureFlag: 'delay-predelivery-comms',
        display: 'Delay Predelivery SMS until warehouse receipt',
        description: 'Dont send predelivery survey until the carrier has received the order',
    },
    {
        featureFlag: 'business-type-autoconfirm',
        display: 'Auto confirm timeframes for business type deliveries',
        description: 'Automatically confirms stop timeframes in planning for business type deliveries.',
    },
    {
        featureFlag: 'disable-tracking-comms',
        display: 'Disable Customer Tracking Links',
        description: 'Do not send tracking links to customers for your orders.',
    },
    {
        featureFlag: 'hide-driver-phone-number',
        display: 'Hide driver phone number on customer tracking links',
        description: 'On the customer tracking link, do not show the driver phone number.',
    },
    {
        featureFlag: APPROXIMATE_FACILITY_LOCATION,
        display: 'Show the pickup facility as an approximate area on tracking links',
        description:
            "On the customer tracking link, the final-mile facility is drawn as a shaded circle covering its general area rather than a pin on its exact address. The customer's own address still shows exactly, and live truck tracking is unaffected once the order leaves the facility.",
    },
    {
        featureFlag: RESEND_UNCONFIRMED_TIMEFRAME_CALLS,
        display: 'Resend Timeframe Confirmation Calls Until Confirmed',
        description:
            'By default, a timeframe confirmation call is only resent when the timeframe changes since the last call. Enabling this will resend confirmation calls even when the timeframe is unchanged, until the customer confirms the timeframe.',
    },
    {
        featureFlag: TIMEFRAME_CONFIRMATION_PREFERENCE,
        type: 'select',
        defaultValue: 'sms',
        display: 'Preferred Timeframe Confirmation Method',
        description:
            'When a dispatcher submits a route using "each shipper\'s preferred method", your orders use this method to confirm delivery timeframes. Defaults to text & email.',
        options: TIMEFRAME_CONFIRMATION_OPTIONS,
    },
];

/**
 * The comms list for a given shipper. Shippers with carrier partners get one extra toggle.
 */
export const shipperCommsFor = ({ partnerCarriers }) => {
    const comms = [...SHIPPER_COMMS];
    if (partnerCarriers?.length > 0) {
        comms.push({
            featureFlag: 'tms-order-completed-email',
            display: 'Receive email notifications when a carrier partner completes an order',
            description: 'Receive email notifications when a carrier partner completes an order.',
        });
    }
    return comms;
};
